import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { api, formatDate } from '../../utils/api'
import { useAuth } from '../../hooks/useAuth'
import { activeUsersFrom } from '../../../../shared/users.js'
import { dealHref } from './DealLink.jsx'

const ISSUE_AREA_COLORS = {
  Collection: { bg: '#fef3c7', color: '#92400e' },
  Product: { bg: '#dbeafe', color: '#1e40af' },
  Deal: { bg: '#dcfce7', color: '#166534' },
  Order: { bg: '#f3e8ff', color: '#7c3aed' },
  Payment: { bg: '#fce7f3', color: '#be185d' },
  Other: { bg: '#f3f4f6', color: '#4b5563' }
}

const ENTITY_TYPE_ICONS = {
  deal: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 16, height: 16 }}>
      <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
    </svg>
  ),
  order: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 16, height: 16 }}>
      <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2" />
      <rect x="9" y="3" width="6" height="4" rx="1" />
      <path d="M9 12h6M9 16h6" />
    </svg>
  ),
  product: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 16, height: 16 }}>
      <path d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
    </svg>
  ),
  ticket: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 16, height: 16 }}>
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
      <path d="M22 6l-10 7L2 6" />
    </svg>
  )
}

function PinnedPanel({ isOpen, onClose, onCountChange }) {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [items, setItems] = useState([])
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(false)
  const [completing, setCompleting] = useState(null)
  const [removing, setRemoving] = useState(null)
  const [filter, setFilter] = useState('mine')

  useEffect(() => {
    if (isOpen) {
      loadPinnedItems()
      if (user?.is_admin) {
        loadUsers()
      }
    }
  }, [isOpen, filter])

  async function loadPinnedItems() {
    setLoading(true)
    try {
      const response = await api.get(`/api/pinned?filter=${filter}`)
      setItems(response.items || [])
      if (onCountChange) {
        onCountChange(response.count || 0)
      }
    } catch (error) {
      console.error('Failed to load pinned items:', error)
    } finally {
      setLoading(false)
    }
  }

  async function loadUsers() {
    try {
      const response = await api.get('/api/users')
      setUsers(activeUsersFrom(response))
    } catch (error) {
      console.error('Failed to load users:', error)
    }
  }

  async function handleReassign(pinId, newAssigneeId) {
    try {
      await api.patch(`/api/pinned/${pinId}/assign`, { assignedTo: Number(newAssigneeId) })
      loadPinnedItems()
    } catch (error) {
      console.error('Failed to reassign pin:', error)
      alert('Failed to reassign pin')
    }
  }

  async function handleComplete(id) {
    setCompleting(id)
    try {
      await api.patch(`/api/pinned/${id}/complete`)
      setItems(prev => prev.filter(item => item.id !== id))
      if (onCountChange) {
        onCountChange(items.length - 1)
      }
    } catch (error) {
      console.error('Failed to complete pin:', error)
      alert('Failed to mark as dealt with')
    } finally {
      setCompleting(null)
    }
  }

  async function handleRemove(id) {
    if (!confirm('Remove this pin without marking as dealt with?')) return
    setRemoving(id)
    try {
      await api.delete(`/api/pinned/${id}`)
      setItems(prev => prev.filter(item => item.id !== id))
      if (onCountChange) {
        onCountChange(items.length - 1)
      }
    } catch (error) {
      console.error('Failed to remove pin:', error)
      alert('Failed to remove pin')
    } finally {
      setRemoving(null)
    }
  }

  function navigateToEntity(item) {
    onClose() // Close the panel first
    if (item.entity_type === 'deal') {
      navigate(dealHref(item.entity_id))
    } else if (item.entity_type === 'order') {
      navigate(`/orders/list?orderId=${item.entity_id}`)
    } else if (item.entity_type === 'product') {
      navigate(`/inventory/list?productId=${item.entity_id}`)
    } else if (item.entity_type === 'ticket') {
      navigate(`/tickets?ticketId=${item.entity_id}`)
    }
  }

  function isOverdue(dueDate) {
    if (!dueDate) return false
    return new Date(dueDate) < new Date(new Date().toDateString())
  }

  function isDueSoon(dueDate) {
    if (!dueDate) return false
    const due = new Date(dueDate)
    const today = new Date(new Date().toDateString())
    const threeDays = new Date(today)
    threeDays.setDate(threeDays.getDate() + 3)
    return due >= today && due <= threeDays
  }

  if (!isOpen) return null

  return (
    <>
      {/* Overlay */}
      <div className="pinned-panel-overlay" onClick={onClose} />

      {/* Panel */}
      <div className="pinned-panel">
        <div className="pinned-panel-header">
          <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 20, height: 20 }}>
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
            </svg>
            Pinned Items
          </h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {user?.is_admin && (
              <select
                className="form-input"
                style={{ fontSize: '0.75rem', padding: '4px 8px', width: 'auto' }}
                value={filter}
                onChange={e => setFilter(e.target.value)}
              >
                <option value="mine">Assigned to Me</option>
                <option value="all">All</option>
                {users.length > 0 && (
                  <optgroup label="By User">
                    {users.map(u => (
                      <option key={u.user_id} value={`user:${u.user_id}`}>{u.name}</option>
                    ))}
                  </optgroup>
                )}
              </select>
            )}
            <button className="modal-close" onClick={onClose}>&times;</button>
          </div>
        </div>

        <div className="pinned-panel-body">
          {loading ? (
            <div style={{ textAlign: 'center', padding: 'var(--space-6)', color: 'var(--color-text-muted)' }}>
              Loading...
            </div>
          ) : items.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 'var(--space-6)', color: 'var(--color-text-muted)' }}>
              No pinned items
            </div>
          ) : (
            <div className="pinned-items-list">
              {items.map(item => {
                const areaStyle = ISSUE_AREA_COLORS[item.issue_area] || ISSUE_AREA_COLORS.Other
                const overdue = isOverdue(item.due_date)
                const dueSoon = isDueSoon(item.due_date)

                return (
                  <div key={item.id} className={`pinned-item ${overdue ? 'pinned-item-overdue' : ''}`}>
                    <div className="pinned-item-header">
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
                        <span style={{ color: 'var(--color-text-muted)' }}>
                          {ENTITY_TYPE_ICONS[item.entity_type]}
                        </span>
                        <button
                          className="pinned-item-name"
                          onClick={() => navigateToEntity(item)}
                        >
                          {item.entityName
                            ? `${item.entityName} (#${item.entity_id})`
                            : `${item.entity_type} #${item.entity_id}`}
                        </button>
                      </div>
                      <span
                        className="badge badge-sm"
                        style={{ background: areaStyle.bg, color: areaStyle.color }}
                      >
                        {item.issue_area}
                      </span>
                    </div>

                    <div className="pinned-item-reason">
                      {item.reason}
                    </div>

                    <div className="pinned-item-meta">
                      <span className="text-muted" style={{ fontSize: '0.75rem' }}>
                        Pinned {formatDate(item.pinned_at)}
                      </span>
                      {item.due_date && (
                        <span
                          className={`pinned-due-date ${overdue ? 'overdue' : dueSoon ? 'due-soon' : ''}`}
                        >
                          Due: {formatDate(item.due_date)}
                        </span>
                      )}
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.75rem' }}>
                      <span className="text-muted">
                        <strong>Assigned to:</strong> {(() => {
                          const assignee = users.find(u => u.user_id === item.assigned_to)
                          if (assignee) return assignee.name
                          return item.assigned_to === user?.user_id ? 'Me' : `User #${item.assigned_to}`
                        })()}
                      </span>
                      {user?.is_admin && users.length > 0 && (
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <span className="text-muted">Reassign:</span>
                          <select
                            className="form-input"
                            style={{ fontSize: '0.75rem', padding: '2px 4px', width: 'auto' }}
                            value={item.assigned_to || ''}
                            onChange={e => handleReassign(item.id, e.target.value)}
                          >
                            {users.map(u => (
                              <option key={u.user_id} value={u.user_id}>{u.name}</option>
                            ))}
                          </select>
                        </span>
                      )}
                    </div>

                    <div className="pinned-item-actions">
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => handleComplete(item.id)}
                        disabled={completing === item.id}
                      >
                        {completing === item.id ? 'Saving...' : 'Dealt With'}
                      </button>
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => handleRemove(item.id)}
                        disabled={removing === item.id}
                      >
                        {removing === item.id ? '...' : 'Remove'}
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export default PinnedPanel
